// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Link } from "react-router-dom"
import type { PlanImpact } from "@bitebalance/contracts"
import { useI18n, formatEurFromCents } from "@/i18n"
import { InfoButton } from "@/components/ui/InfoButton"
import { goalStatusWithDelta } from "@/lib/glossary"

/**
 * Budget Model v2 (ADR-052 §7.2, owner D-2): a proposal whose resulting
 * checkout would exceed the HARD ceiling is a BLOCKING notice — there is no
 * consent box, no "allow just this once". The user changes or removes the
 * maximum in Settings. Rendered from `impact.checkoutConflict` at preview
 * time and again from a 422 MAX_CHECKOUT_CONFLICT at apply time (same copy,
 * same numbers).
 */
export function CheckoutConflictNotice({
  overCents,
  maxCents,
}: {
  overCents: number | null
  maxCents: number | null
}) {
  const { t, locale } = useI18n()
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-bb-lg border border-gold/50 bg-gold/10 p-3 text-sm text-ink"
    >
      <div className="flex items-start gap-2">
        <p className="flex-1">
          {t("views.budget.planCheckoutConflict", {
            over: formatEurFromCents(Math.max(0, overCents ?? 0), locale),
            max: formatEurFromCents(maxCents ?? 0, locale),
          })}
        </p>
        <InfoButton termId="maximum-checkout" />
      </div>
      <Link to="/app/more/preferences" className="w-fit text-sm font-semibold text-green underline">
        {t("views.budget.planMaxInfeasibleCta")}
      </Link>
    </div>
  )
}

/** Numbers from a proposal impact for the conflict notice (v2 checkout
 * verdict first; the pre-v2 gap/budget pair as a last resort). */
export function conflictAmounts(impact: PlanImpact | undefined): {
  overCents: number | null
  maxCents: number | null
} {
  const checkout = impact?.budget?.checkout
  if (checkout) return { overCents: checkout.deltaCents, maxCents: checkout.maxCents }
  return { overCents: impact?.budget?.gapCents ?? null, maxCents: impact?.budget?.budgetCents ?? null }
}

/**
 * Budget Model v2: the SOFT goal impact of a proposal — before → after
 * forecast weekly average with the canonical status word and the exact
 * delta. Pure information: never a block, never a consent.
 */
export function GoalImpactLine({ goalImpact }: { goalImpact: NonNullable<PlanImpact["goalImpact"]> }) {
  const { t, locale } = useI18n()
  const { beforeForecastWeeklyAverageCents: before, afterForecastWeeklyAverageCents: after, goalCents } =
    goalImpact
  const money = (cents: number | null) => (cents === null ? "—" : formatEurFromCents(cents, locale))
  const delta = after !== null && goalCents !== null ? after - goalCents : null
  return (
    <p role="status" className="flex items-center gap-2 text-xs text-ink-2">
      <span>
        {t("views.budget.planGoalImpact", {
          before: money(before),
          after: money(after),
          status: goalStatusWithDelta(goalImpact.afterStatus, delta, locale),
        })}
      </span>
      <InfoButton termId="forecast-weekly-average" />
    </p>
  )
}
